// Save and share for a chat photo (ART-267), one rule per platform so the lightbox never has to know which it is on:
//
//   iOS app      → native: Save adds the photo to Photos, Share opens the system share sheet. A browser-style
//                  `<a download>` does nothing inside the app, which is why this exists.
//   browser      → Save downloads the file (the bytes are already in the page as an object URL, so the link
//                  carries no auth problem); Share uses the Web Share API where it can share FILES, and is
//                  simply not offered where it cannot.
import { nativeImageActionsAvailable, saveImageNative, shareImageNative } from "@cntrl/native";
import { pushToast } from "../../app/toasts.js";

/** Read an object URL's bytes back out — as base64 for the native bridge, or as a File for the Web Share API. */
async function blobOf(url: string): Promise<Blob> {
  return (await fetch(url)).blob();
}

function base64Of(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/** Whether Share can be offered here at all — natively, or by a browser that can share files. */
export function canShareImage(): boolean {
  if (nativeImageActionsAvailable()) return true;
  if (typeof navigator === "undefined" || typeof navigator.canShare !== "function") return false;
  try {
    return navigator.canShare({ files: [new File([new Uint8Array(1)], "photo.jpg", { type: "image/jpeg" })] });
  } catch {
    return false;
  }
}

export async function saveImage(url: string, fileName: string): Promise<void> {
  if (nativeImageActionsAvailable()) {
    try {
      await saveImageNative(await base64Of(await blobOf(url)));
      pushToast("Saved to Photos", "success");
    } catch (err) {
      const denied = (err as { code?: string } | null)?.code === "PHOTOS_DENIED";
      pushToast(denied ? "Allow Photos access in Settings to save images" : "Couldn't save that photo");
    }
    return;
  }
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export async function shareImage(url: string, fileName: string): Promise<void> {
  try {
    const blob = await blobOf(url);
    if (nativeImageActionsAvailable()) {
      await shareImageNative(await base64Of(blob));
      return;
    }
    await navigator.share({ files: [new File([blob], fileName, { type: blob.type || "image/jpeg" })] });
  } catch (err) {
    // Dismissing the browser's share sheet rejects with AbortError — that is the person changing their mind.
    if ((err as { name?: string } | null)?.name !== "AbortError") pushToast("Couldn't share that photo");
  }
}
